/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  inject,
  Injectable,
  OnDestroy,
  signal,
  WritableSignal,
} from '@angular/core';
import { NotificationService } from '@api-client';
import { EventSourcePolyfill } from 'event-source-polyfill';
import { environment } from '../../environments/environment';
import { ConnectEventSourceOptions } from './interfaces/interface';

@Injectable({ providedIn: 'root' })
export class SseNotificationStore implements OnDestroy {
  private readonly notificationService = inject(NotificationService);

  private readonly eventSourceUrl: string = `${environment.rootUrl}/api/v1/notification/send/user`;
  private readonly retryInterval: number = 3000;

  private userId: WritableSignal<string> = signal<string>('');
  private token: WritableSignal<string> = signal<string>('');
  private eventSource: EventSourcePolyfill | null = null;

  public count = signal<number>(0);

  ngOnDestroy(): void {
    this.closeSSE();
  }

  /**
   * @name fetchState
   * @description 서버에서 알림 개수 조회 및 상태 업데이트
   * @returns {void}
   */
  public fetchState(): void {
    this.notificationService
      .notificationControllerCount({
        pageNo: 1,
        pageSize: 9999,
      })
      .subscribe({
        next: async (count: number) => {
          this.count.set(count);
        },
        error: error => {
          console.error('Error fetching notification count:', error);
        },
      });
  }

  /**
   * @name connectSSE
   * @description SSE 연결 시작 및 실시간 알림 수신
   * @param {ConnectEventSourceOptions} connectEventSourceOptions - SSE 연결 옵션
   * @returns {Promise<void>}
   */
  public async connectSSE(
    connectEventSourceOptions: ConnectEventSourceOptions
  ): Promise<void> {
    const { userId, token } = connectEventSourceOptions;
    if (!userId || !token) return;
    if (this.eventSource) return;
    this.userId.set(userId);
    this.token.set(token);
    this.eventSource = new EventSourcePolyfill(this.eventSourceUrl, {
      headers: {
        Authorization: `Bearer ${this.token()}`,
      },
    });

    // SSE 연결 성공
    this.eventSource.onopen = () => {
      console.debug('SSE connection success');
      this.fetchState();
    };

    // SSE 메시지 수신
    this.eventSource.onmessage = async (event: any) => {
      console.debug('SSE Message Received:', event);
      if (event?.data === userId) {
        console.debug('SSE Message for User:', userId);
        this.fetchState();
      }
    };

    // SSE 연결 에러 - 자동 재연결
    this.eventSource.onerror = () => {
      console.error('SSE connection error, reconnecting...');
      this.closeSSE();
      this.eventSource = null;
      setTimeout(async () => {
        await this.connectSSE({
          userId: this.userId(),
          token: this.token(),
        });
      }, this.retryInterval);
    };
  }

  /**
   * @name closeSSE
   * @description SSE 연결 종료
   * @returns {void}
   */
  public closeSSE(): void {
    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
      console.debug('SSE connection closed');
    }
  }

  /**
   * @name clear
   * @description 알림 카운트 및 배지 초기화
   * @returns {Promise<void>}
   */
  public async clear(): Promise<void> {
    this.count.set(0);
  }
}
